import { useEffect, useSyncExternalStore } from "react";
import { HardDrive } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtBytes } from "@/components/OneDriveQuota";

let usage = null;
const subs = new Set();
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const refreshUsage = async () => {
  try { const { data } = await api.get("/storage/usage"); usage = data; subs.forEach((f) => f()); } catch { /* ignore */ }
};

export const usageTone = (used, limit) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return { pct, tone: pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500" };
};

export const UsageBubble = ({ used = 0, limit, testid = "storage-pill", className = "", scope = "" }) => {
  const { pct, tone } = usageTone(used, limit);
  const label = limit ? `${fmtBytes(Math.max(0, limit - used))} free${scope}` : `${fmtBytes(used)} used`;
  const alert = pct >= 90 ? "border-red-300" : pct >= 70 ? "border-amber-300" : "border-zinc-200";
  return (
    <div className={`inline-flex items-center gap-3 rounded-full border bg-white px-4 py-2 ${alert} ${className}`} data-testid={testid}
      title={limit ? `${fmtBytes(used)} of ${fmtBytes(limit)} used in Resonance storage${scope}` : `${fmtBytes(used)} used in Resonance storage`}>
      <HardDrive size={13} className={pct >= 90 ? "text-red-600" : "text-zinc-500"} />
      <span className="whitespace-nowrap text-xs text-zinc-700" data-testid={`${testid}-label`}>{label}</span>
      {limit != null && (
        <>
          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-100"><div className={`h-full rounded-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} data-testid={`${testid}-bar`} /></div>
          <span className="font-mono text-[11px] text-zinc-500" data-testid={`${testid}-pct`}>{pct}%</span>
        </>
      )}
    </div>
  );
};

export const StoragePill = () => {
  const { user } = useAuth();
  const u = useSyncExternalStore(subscribe, () => usage);
  useEffect(() => { refreshUsage(); }, []);
  if (!u || !u.allowed) return null;
  if (user?.role === "owner") return u.pool ? <UsageBubble used={u.pool.used} limit={u.pool.limit} scope=" · all accounts" testid="storage-pill-pool" /> : null;
  return <UsageBubble used={u.used} limit={u.limit} />;
};

/* Compact block for the sidebar, right under the brand. */
export const SidebarStorage = () => {
  const { user } = useAuth();
  const u = useSyncExternalStore(subscribe, () => usage);
  useEffect(() => { refreshUsage(); }, []);
  if (!u || !u.allowed) return null;
  const owner = user?.role === "owner";
  if (owner && !u.pool) return null;
  const used = owner ? u.pool.used : u.used;
  const limit = owner ? u.pool.limit : u.limit;
  const { pct, tone } = usageTone(used, limit);
  const text = pct >= 90 ? "text-red-600" : "text-zinc-500";
  return (
    <div className="mx-3 mt-3 rounded-md border border-zinc-200/80 bg-white/70 px-3 py-2" data-testid="sidebar-storage"
      title={limit ? `${fmtBytes(used)} of ${fmtBytes(limit)} used in Resonance storage${owner ? " · all accounts" : ""}` : `${fmtBytes(used)} used in Resonance storage`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`flex items-center gap-1.5 text-[11px] ${text}`}><HardDrive size={11} />{limit ? `${fmtBytes(Math.max(0, limit - used))} free` : `${fmtBytes(used)} used`}</span>
        {limit != null && <span className="font-mono text-[10px] text-zinc-400" data-testid="sidebar-storage-pct">{pct}%</span>}
      </div>
      {limit != null && <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-zinc-100"><div className={`h-full rounded-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>}
    </div>
  );
};
